import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  from,
  of,
  switchMap,
  throwError,
  tap,
} from 'rxjs';
import { ContextService } from '@maximizer/core/shared/data-access';
import { Octopus } from '@maximizer/core/shared/domain';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { InsightsService } from '@maximizer/core/shared/insights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { OAuth2Service } from '../services/oauth2.service';
import { PopupSessionExpiredService } from '../services/popup-session-expired.service';
import { IS_BROWSER_ONLY_ROUTE } from '../tokens/browser-only-route.token';
import { OAUTH2_PATHS } from '../models/constants';

type AuthFailureSource = 'status401' | 'body-invalid-auth';

class AuthRecoveryError extends Error {
  constructor(public readonly source: AuthFailureSource) {
    super('Authentication failed - invalid token in response');
    this.name = 'AuthRecoveryError';
  }
}

class TokenRefreshFailedError extends Error {
  constructor(public readonly reason?: string) {
    super('Token refresh failed');
    this.name = 'TokenRefreshFailedError';
  }
}

@Injectable()
export class OAuth2Interceptor implements HttpInterceptor {
  private readonly isBrowserOnlyRoute = inject(IS_BROWSER_ONLY_ROUTE);
  private readonly popupSessionExpired = inject(PopupSessionExpiredService);
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly REFRESH_WAIT_TIMEOUT_MS = 15_000;
  private readonly outlookSyncUrl = '/integration/outlook';

  constructor(
    private readonly oauth2Service: OAuth2Service,
    private readonly context: ContextService,
    private readonly insightsService: InsightsService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isOAuthEndpointRequest(request)) {
      return next.handle(request);
    }

    if (!this.requiresAuthentication(request)) {
      return next.handle(request);
    }

    // Once the popup is session-expired, error locally without hitting `next.handle`.
    const isExpired = this.popupSessionExpired.expired();
    if (this.isBrowserOnlyRoute && isExpired !== null) {
      return throwError(() => new Error('popup-session-expired'));
    }

    if (this.isBrowserOnlyRoute) {
      return this.handleBrowserOnlyRequest(request, next);
    }

    return this.waitForActiveRefresh(
      request,
      'oauth2-interceptor-intercept',
    ).pipe(switchMap(() => this.handleRequest(request, next, 0)));
  }

  private handleBrowserOnlyRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const authorizedRequest = this.addAuthHeader(request);

    return next.handle(authorizedRequest).pipe(
      this.authResponseTap(
        request,
        authorizedRequest,
        'oauth2-interceptor-browser-only-authentication-failed',
      ),
      catchError((error) => {
        const authFailureSource = this.getAuthFailureSource(error);
        if (authFailureSource) {
          this.popupSessionExpired.expire(
            authFailureSource === 'status401' ? '401' : 'authentication-error',
          );
          this.trackTrace(
            'oauth2-interceptor-browser-only-auth-failure-passthrough',
            this.requestTelemetry(request, {
              authFailureSource,
              retryCount: 0,
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  private handleRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
    retryCount: number,
  ): Observable<HttpEvent<any>> {
    const authorizedRequest = this.addAuthHeader(request);

    return next.handle(authorizedRequest).pipe(
      this.authResponseTap(
        request,
        authorizedRequest,
        'oauth2-interceptor-authentication-failed',
      ),
      catchError((error) =>
        this.handleError(error, request, authorizedRequest, next, retryCount),
      ),
    );
  }

  private authResponseTap(
    request: HttpRequest<any>,
    authorizedRequest: HttpRequest<any>,
    traceMessage: string,
  ) {
    return tap((event: HttpEvent<any>) => {
      if (
        event instanceof HttpResponse &&
        this.isInvalidAuthentication(event)
      ) {
        this.trackTrace(
          traceMessage,
          this.requestTelemetry(request, {
            authFailureSource: 'body-invalid-auth',
          }),
        );
        throw new AuthRecoveryError('body-invalid-auth');
      }

      if (
        event instanceof HttpResponse &&
        authorizedRequest.headers.has('Authorization') &&
        this.isAllowlistedOrigin(request)
      ) {
        this.oauth2Service.recordAuthenticatedUse();
      }
    });
  }

  private requiresAuthentication(request: HttpRequest<any>): boolean {
    return (
      this.isAllowlistedOrigin(request) ||
      this.isFerretRequest(request) ||
      this.isOutlookSyncRequest(request)
    );
  }

  private isAllowlistedOrigin(request: HttpRequest<any>): boolean {
    const apiUrl = this.context.api;
    if (!apiUrl) return false;

    try {
      const apiOrigin = new URL(apiUrl).origin;
      return request.url.startsWith(apiOrigin);
    } catch {
      return request.url.startsWith(apiUrl);
    }
  }

  private isOAuthEndpointRequest(request: HttpRequest<any>): boolean {
    const requestPath = this.getPathname(request.url);
    if (!requestPath) {
      return false;
    }

    const oauthPaths = [
      OAUTH2_PATHS.TOKEN,
      OAUTH2_PATHS.AUTHORIZE,
      OAUTH2_PATHS.ON_PREM_TOKEN,
      OAUTH2_PATHS.ON_PREM_AUTHORIZE,
    ].map((path) => path.toLowerCase());

    const normalizedPath = requestPath.toLowerCase();
    return oauthPaths.some((path) => normalizedPath.endsWith(path));
  }

  private getPathname(url: string): string {
    if (!url) {
      return '';
    }

    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      const pathWithoutQuery = url.split('?')[0] ?? '';
      return pathWithoutQuery.split('#')[0] ?? '';
    }
  }

  private addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.context.token;

    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private handleError(
    error: HttpErrorResponse | Error,
    originalRequest: HttpRequest<any>,
    failedRequest: HttpRequest<any>,
    next: HttpHandler,
    retryCount: number,
  ): Observable<HttpEvent<any>> {
    const authFailureSource = this.getAuthFailureSource(error);

    if (!authFailureSource) {
      return throwError(() => error);
    }

    if (
      this.oauth2Service.isRefreshActive() &&
      retryCount < this.MAX_RETRY_ATTEMPTS
    ) {
      return this.waitForActiveRefresh(
        originalRequest,
        'oauth2-interceptor-auth-refresh-active-wait',
        authFailureSource,
      ).pipe(
        switchMap(() =>
          this.handleRequest(originalRequest, next, retryCount + 1),
        ),
      );
    }

    if (retryCount < this.MAX_RETRY_ATTEMPTS) {
      return this.retryAuthFailure(
        originalRequest,
        failedRequest,
        next,
        retryCount,
        authFailureSource,
      );
    }

    this.trackException(
      'oauth2-interceptor-auth-retry-exhausted',
      error,
      this.requestTelemetry(originalRequest, {
        authFailureSource,
        retryCount,
      }),
    );

    return throwError(() => error);
  }

  private retryAuthFailure(
    originalRequest: HttpRequest<any>,
    failedRequest: HttpRequest<any>,
    next: HttpHandler,
    retryCount: number,
    authFailureSource: AuthFailureSource,
  ): Observable<HttpEvent<any>> {
    const retryWithCurrentToken =
      this.shouldRetryWithCurrentToken(failedRequest);
    const retryEvent = retryWithCurrentToken
      ? 'oauth2-interceptor-stale-token-retry-without-refresh'
      : 'oauth2-interceptor-auth-retry';

    this.trackTrace(
      retryEvent,
      this.requestTelemetry(originalRequest, {
        authFailureSource,
        retryCount,
      }),
    );

    if (retryWithCurrentToken) {
      return this.handleRequest(originalRequest, next, retryCount + 1);
    }

    return this.refreshTokenAndRetry(
      originalRequest,
      next,
      retryCount + 1,
      authFailureSource,
    );
  }

  private refreshTokenAndRetry(
    originalRequest: HttpRequest<any>,
    next: HttpHandler,
    retryCount: number,
    authFailureSource: AuthFailureSource,
  ): Observable<HttpEvent<any>> {
    const callerTag = 'oauth2-interceptor-auth-retry';
    this.trackTrace(
      'oauth2-interceptor-refresh-start',
      this.requestTelemetry(originalRequest, {
        authFailureSource,
        callerTag,
      }),
    );

    return this.oauth2Service.refreshToken(undefined, true, callerTag).pipe(
      switchMap((token) => {
        if (!token || !this.context.token) {
          const reason = this.oauth2Service.getLastRefreshFailureReason();
          return throwError(() => new TokenRefreshFailedError(reason));
        }

        this.trackTrace(
          'oauth2-interceptor-refresh-success',
          this.requestTelemetry(originalRequest, {
            authFailureSource,
            retryCount,
          }),
        );
        return this.handleRequest(originalRequest, next, retryCount);
      }),
      catchError((error) => {
        const normalizedError =
          error instanceof Error ? error : new Error('Token refresh failed');
        const reason =
          error instanceof TokenRefreshFailedError ? error.reason : undefined;

        this.trackException(
          'oauth2-interceptor-refresh-failed',
          error,
          this.requestTelemetry(originalRequest, {
            authFailureSource,
            reason: reason ?? 'UNKNOWN',
          }),
        );
        return throwError(() => normalizedError);
      }),
    );
  }

  private waitForActiveRefresh(
    request: HttpRequest<any>,
    callerTag: string,
    authFailureSource?: AuthFailureSource,
  ): Observable<void> {
    if (!this.oauth2Service.isRefreshActive()) {
      return of(undefined);
    }

    this.trackTrace(
      'oauth2-interceptor-refresh-active-wait',
      this.requestTelemetry(request, {
        callerTag,
        authFailureSource,
        timeoutMs: this.REFRESH_WAIT_TIMEOUT_MS,
      }),
    );

    return from(
      this.oauth2Service.waitForActiveRefresh(this.REFRESH_WAIT_TIMEOUT_MS),
    ).pipe(
      switchMap(() => of(undefined)),
      catchError((error) => {
        this.trackException(
          'oauth2-interceptor-refresh-active-wait-failed',
          error,
          this.requestTelemetry(request, {
            callerTag,
            authFailureSource,
          }),
        );
        return of(undefined);
      }),
    );
  }

  private getAuthFailureSource(
    error: HttpErrorResponse | Error,
  ): AuthFailureSource | null {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      return 'status401';
    }

    if (error instanceof AuthRecoveryError) {
      return error.source;
    }

    return null;
  }

  private isInvalidAuthentication(event: HttpResponse<any>): boolean {
    const response = event.body as Octopus.Response;
    const invalidToken = 'invalid token';

    if (!response) return false;

    const code = response.Code;
    const messages = response.Msg ?? [];

    const invalid = code === Octopus.ResponseStatusCode.InvalidAuthentication;
    const unsuccessful = code === Octopus.ResponseStatusCode.Unsuccessful;

    return (
      invalid ||
      (unsuccessful &&
        messages.some(
          (error: string | Octopus.ResponseError) =>
            (typeof error === 'string' &&
              error.toLowerCase().includes(invalidToken)) ||
            (error as Octopus.ResponseError).Message?.toLowerCase().includes(
              invalidToken,
            ),
        ))
    );
  }

  private isFerretRequest(request: HttpRequest<any>): boolean {
    return (
      !isNullOrEmpty(this.context.ferretUrl) &&
      request.url?.startsWith(this.context.ferretUrl)
    );
  }

  private isOutlookSyncRequest(request: HttpRequest<any>): boolean {
    const lowercaseUrl = request.url?.toLowerCase() ?? '';
    if (isNullOrEmpty(lowercaseUrl)) return false;
    return lowercaseUrl.includes(this.outlookSyncUrl);
  }

  private shouldRetryWithCurrentToken(
    failedRequest: HttpRequest<any>,
  ): boolean {
    const failedToken = this.extractBearerToken(failedRequest);
    const currentToken = this.context.token;
    return !!failedToken && !!currentToken && failedToken !== currentToken;
  }

  private extractBearerToken(request: HttpRequest<any>): string | null {
    const authorizationHeader = request.headers.get('Authorization');
    if (!authorizationHeader) {
      return null;
    }

    const bearerMatch = /^Bearer\s+([^\s]+)$/i.exec(authorizationHeader);
    if (!bearerMatch) {
      return null;
    }

    return bearerMatch[1];
  }

  private getSafeUrl(request: HttpRequest<any>): string {
    const url = request.url ?? '';
    return url.split('?')[0];
  }

  private requestTelemetry(
    request: HttpRequest<any>,
    properties?: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      url: this.getSafeUrl(request),
      ...properties,
    };
  }

  private trackTrace(
    message: string,
    properties?: Record<string, unknown>,
    severityLevel: SeverityLevel = SeverityLevel.Verbose,
  ): void {
    this.insightsService.trackTrace(
      { message, severityLevel },
      this.withBaseTelemetry({
        ...properties,
        level: SeverityLevel[severityLevel],
      }),
    );
  }

  private trackException(
    event: string,
    error: unknown,
    properties?: Record<string, unknown>,
  ): void {
    const exception = error instanceof Error ? error : new Error(String(error));
    this.insightsService.trackException(
      { error: exception },
      this.withBaseTelemetry({ event, ...properties }),
    );
  }

  private withBaseTelemetry(
    properties?: Record<string, unknown>,
  ): Record<string, unknown> {
    return {
      component: 'OAuth2Interceptor',
      hasToken: !!this.context.token,
      ...properties,
    };
  }
}
